/*
Profile screen — stats + backup ZIP export/import.

Mirrors the Android ProfileScreen at the structural level:
- Stats card: total captures, distinct species, distinct remarquables,
  first capture date.
- Backup card: Export / Import buttons feeding the same ZIP format the
  Android app reads (BackupModels.kt).

Skipped vs. Android (out of skeleton scope):
- Global / Saison vive segmented filter — single-season MVP.
- Badge card — the BadgeEvaluator pipeline is its own feature.
- "Comment jouer" link — no onboarding / WELCOME_REPLAY route yet.
*/

import { navigate, kPaths } from '../router'
import { listCaptures } from '../data/captureRepository'
import { ensureSpeciesIndex, speciesNameFor } from '../data/speciesIndex'
import { exportBackup, type ExportResult } from '../backup/exporter'
import { importBackup, type ImportResult } from '../backup/importer'
import { markDiscoveryDirty } from './MapScreen'
import { ensureArbresFeatures, ordinaryCountsBySpecies } from '../data/arbresFeatures'
import { ensureArbreAttrs } from '../data/arbreAttrs'
import { ensureSpeciesInfo } from '../data/speciesInfo'
import { ensureGenreInfo } from '../data/genreInfo'
import { ensureArrSpecies, arrKeysWithRemarquables } from '../data/arrSpecies'
import { evaluateBadges } from '../data/badgeEvaluator'
import { buildHistorySeries, type ProgressionMetric } from '../data/progressionHistory'
import {
	flattenBadgeUnlocks,
	glyphFor,
	type BadgeState,
	type BadgeUnlock,
} from '../data/badge'
import { estimateStorage } from '../data/persistentStorage'
import { ensureDatasetStats } from '../data/datasetStats'
import { renderEmptyState } from './components/EmptyState'
import { renderProgressBar } from './components/ProgressBar'
import { renderSkeletonBlock, renderSkeletonCircle } from './components/Skeleton'
import { allGenres, genreHasAnyCapture } from '../data/speciesIndex'
import { arrFor } from '../data/arbreAttrs'
import { arrKeyIdSlug } from '../data/arrKey'
import { familierGenres, FAMILIER_GENRE_PREFIX, FAMILIER_ARR_PREFIX } from '../data/badge'

const kFrenchLongDate = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })  // "9 mai 2026" footer date

const kQuotaWarnFraction: number = 0.8  // banner shows when usage / quota crosses this — iOS tends to evict around here
const kMegaByte: number = 1024 * 1024  // formatting helper for the storage banner copy

const kMillisPerDay: number = 24 * 60 * 60 * 1000

const kExportTimeoutMs: number = 60_000  // Android v1.0.1: barre de progression export/import passe en mode "longue durée" après 60 s

/*
Stats de progression — alimentent les 7 barres ProgressionCard (mirror Android
v1.2.0 ProfileScreen.kt:494-500). Calculées en un seul pass sur les
captures + une éval `evaluateBadges` partagée avec la preview "Derniers
badges" (mémoïsée).
*/
interface Stats
{
	totalCaptures: number                // total captures (informatif, plus rendu directement)
	arbresUnlocked: number               // arbres ordinaires des espèces capturées + remarquables capturés (parité nombreArbresDecouverts) — barre 1
	totalArbres: number                  // 217 264 v1.3.2 — dénominateur barre 1
	remarquablesCount: number            // distinct remarquable arbreId — barre 2
	totalRemarquables: number            // 183 v1.3.2 — dénominateur barre 2
	distinctIdentifiedSpecies: number    // distinct sk identifiés — barre 3 num
	totalEspecesIdentifiees: number      // 784 v1.3.2 — dénominateur barre 3
	genresDecouverts: number             // genres touchés (v1.3.1 genreHasAnyCapture) — barre 4 num
	totalGenres: number                  // 204 v1.3.2 — dénominateur barre 4 (allGenres() exclut « Non spécifié »)
	genresComplets: number               // count familier_genre_* unlocked — barre 5 num
	totalGenresMajeurs: number           // familierGenres().length (26) — dénominateur barre 5
	arrVisites: number                   // distinct ArrKey slug (hors `other`) — barre 6 num
	totalArrVisites: number              // 22 (20 arr + 2 bois) — dénominateur barre 6
	arrComplets: number                  // count familier_arr_* unlocked — barre 7 num
	totalArrComplets: number             // 20 (v1.4.0 : 2 arrs sans remarquables exclus) — dénominateur barre 7
	daysSinceFirst: number               // jours depuis 1re capture (DaysSinceLine), 0 si aucune
}

/*
Mount the profile screen. Renders a static shell synchronously, then
hydrates stats + badge preview async — same pattern as the Arboretum
so back-nav feels instant.
*/
let fActiveObjectUrls: string[] = []  // timeline thumbnail blob URLs created by the current mount, revoked on remount
let fHistoryController: { destroy(): void } | null = null  // open HistorySheet, dismissed before remount

export function mountProfileScreen(inRoot: HTMLElement): void
{
	revokeObjectUrls()  // clean up timeline thumbnails left by the previous mount
	if (fHistoryController) { fHistoryController.destroy(); fHistoryController = null }

	inRoot.innerHTML = renderShell()
	wireBackNav(inRoot)
	wireBackup(inRoot)
	void hydrateStats(inRoot)
	void hydrateBadgesPreview(inRoot)
	void hydrateStorageWarning(inRoot)
	void hydrateDatasetVersion(inRoot)
	wireProgressionLongPress(inRoot)
}

function revokeObjectUrls(): void
{
	for (const vUrl of fActiveObjectUrls) URL.revokeObjectURL(vUrl)
	fActiveObjectUrls = []
}

/*
Skeleton placeholders shaped like the real Profile sub-views. Render
shape mirrors the populated layout 1:1 so the page doesn't reflow
when the IDB reads resolve.
*/

function renderProfileStatsSkeleton(): string
{
	// 7 lignes de barres de progression — match la hauteur du
	// ProgressionCard hydraté. DaysSinceLine au-dessus en largeur 60 %.
	return `
		<div class="progression-days-line progression-days-line-skeleton">
			${renderSkeletonBlock({ width: '60%', height: '13px' })}
		</div>
	` + [0, 1, 2, 3, 4, 5, 6].map(() => `
		<div class="progression-bar progression-bar-skeleton">
			${renderSkeletonBlock({ width: '50%', height: '14px' })}
			${renderSkeletonBlock({ width: '100%', height: '12px', radius: '6px' })}
		</div>
	`).join('')
}

function renderBadgesPreviewSkeleton(): string
{
	return [0, 1, 2].map(() => `
		<div class="profile-badge-mini profile-badge-mini-skeleton">
			${renderSkeletonCircle('40px')}
			${renderSkeletonBlock({ width: '70%', height: '10px' })}
		</div>
	`).join('')
}

function renderShell(): string
{
	return `
		<div class="screen profile">
			<header class="arboretum-header">
				<button class="nav-back" id="nav-back" aria-label="Retour">←</button>
				<h1 class="display arboretum-title">Profil</h1>
			</header>

			<div class="profile-body">
				<aside class="profile-quota-banner" id="profile-quota-banner" data-open="false" role="status">
					<span class="profile-quota-banner-icon" aria-hidden="true">📦</span>
					<div class="profile-quota-banner-body">
						<strong class="profile-quota-banner-title">Sauvegarde recommandée</strong>
						<span class="profile-quota-banner-text" id="profile-quota-banner-text"></span>
					</div>
					<button class="profile-quota-banner-cta" type="button" id="profile-quota-banner-cta">Exporter</button>
				</aside>

				<div id="profile-empty" hidden></div>
				<div class="progression-days-line" id="days-line" hidden></div>
				<h2 class="profile-section-title" id="progression-heading">Progression</h2>
				<section class="profile-card progression-card" id="stats-card">
					<div class="progression-list" id="stats-grid">
						${renderProfileStatsSkeleton()}
					</div>
				</section>

				<h2 class="profile-section-title">Badges</h2>
				<section class="profile-card">
					<div class="profile-badges-preview" id="badges-preview">
						${renderBadgesPreviewSkeleton()}
					</div>
					<div class="profile-actions">
						<button class="profile-button profile-button-primary" id="btn-badges">🏅 Voir tous les badges</button>
					</div>
				</section>

				<h2 class="profile-section-title">Infos</h2>
				<section class="profile-card">
					<div class="profile-actions profile-actions-stack">
						<button class="profile-button" id="btn-howto">❓ Comment jouer</button>
						<button class="profile-button" id="btn-about">ℹ️ À propos</button>
						<button class="profile-button" id="btn-privacy">🔒 Confidentialité</button>
						<button class="profile-button" id="btn-terms">📜 Conditions d'utilisation</button>
					</div>
				</section>

				<h2 class="profile-section-title">Sauvegarde</h2>
				<section class="profile-card">
					<p class="profile-section-blurb">
						Exporte tes captures dans un ZIP — même format que l'app Android,
						compatible dans les deux sens. Aucun envoi extérieur, le fichier
						reste sur ton appareil.
					</p>
					<div class="profile-actions">
						<button class="profile-button profile-button-primary" id="btn-export">📦 Exporter mes captures</button>
						<button class="profile-button" id="btn-import">📥 Importer un backup</button>
					</div>
					<div class="profile-status" id="backup-status" data-variant="info"></div>
					<input type="file" id="import-input" accept=".zip,application/zip,application/octet-stream" hidden />
				</section>

				<footer class="profile-footer" id="profile-footer">
					<span class="profile-footer-line">Données : <a class="profile-footer-link" href="https://opendata.paris.fr/explore/dataset/les-arbres/" target="_blank" rel="noopener">OpenData Paris</a> (ODbL)</span>
					<span class="profile-footer-line" id="profile-footer-dataset">Snapshot…</span>
				</footer>
			</div>
		</div>
	`
}

function wireBackNav(inRoot: HTMLElement): void
{
	inRoot.querySelector('#nav-back')?.addEventListener('click', () => navigate(kPaths.map()))
	inRoot.querySelector('#btn-badges')?.addEventListener('click', () => navigate(kPaths.badges()))
	inRoot.querySelector('#btn-howto')?.addEventListener('click', () => navigate(kPaths.welcome(true)))
	inRoot.querySelector('#btn-about')?.addEventListener('click', () => navigate(kPaths.about()))
	inRoot.querySelector('#btn-privacy')?.addEventListener('click', () => navigate(kPaths.privacy()))
	inRoot.querySelector('#btn-terms')?.addEventListener('click', () => navigate(kPaths.terms()))
}

/*
Wire the export / import buttons. Export runs immediately. Import opens
a file picker, then runs once a file is selected — the picker is the
PWA's analogue of the Android Storage Access Framework's ACTION_OPEN_DOCUMENT.

Android v1.0.1 added a per-step label + a 60 s fallback message ("L'opération
prend plus de temps que prévu…") so a slow backup doesn't look frozen.
We wire the same: pass an onProgress to exporter/importer, arm a 60 s timer
that promotes the label text once it expires.
*/
function wireBackup(inRoot: HTMLElement): void
{
	inRoot.querySelector('#btn-export')?.addEventListener('click', async () => {
		const vCancelLongTimer = armLongRunningTimer(inRoot)
		setStatus(inRoot, 'Export en cours…', 'info')
		try
		{
			const vResult = await exportBackup((inLabel) => setStatus(inRoot, inLabel, 'info'))
			vCancelLongTimer()
			setStatus(inRoot, formatExportSummary(vResult), 'success')
		}
		catch (vErr)
		{
			vCancelLongTimer()
			const vMsg = vErr instanceof Error ? vErr.message : 'Erreur inconnue'
			setStatus(inRoot, `Échec de l'export : ${vMsg}`, 'error')
		}
	})

	const vInput = inRoot.querySelector<HTMLInputElement>('#import-input')
	inRoot.querySelector('#btn-import')?.addEventListener('click', () => {
		if (vInput) { vInput.value = ''; vInput.click() }
	})
	vInput?.addEventListener('change', async () => {
		const vFile = vInput.files?.[0]  // first (and only) selected file
		if (!vFile) return
		const vCancelLongTimer = armLongRunningTimer(inRoot)
		setStatus(inRoot, 'Import en cours…', 'info')
		try
		{
			// Pre-warm the trees index so any Arboretum visit after the
			// import finds the species names cached.
			void ensureArbresFeatures().catch(() => { /* not fatal for import */ })

			const vResult = await importBackup(vFile, (inLabel) => setStatus(inRoot, inLabel, 'info'))
			vCancelLongTimer()
			setStatus(inRoot, formatImportSummary(vResult), 'success')
			// La découverte a changé : le prochain mount de la carte doit
			// re-enrichir species-wide (sous splash conditionnel) — sans ce
			// flag la carte persistante garderait les nouvelles espèces grises.
			if (vResult.importedCount > 0) markDiscoveryDirty()
			void hydrateStats(inRoot)  // refresh stats now that captures may have grown
			void hydrateBadgesPreview(inRoot)  // idem pour les derniers badges — sinon preview figée
		}
		catch (vErr)
		{
			vCancelLongTimer()
			const vMsg = vErr instanceof Error ? vErr.message : 'Erreur inconnue'
			setStatus(inRoot, `Échec de l'import : ${vMsg}`, 'error')
		}
	})
}

/*
Arm the 60 s "longue durée" promo. Returns a cancel closure the caller
invokes on completion or error so the timer doesn't fire after the
fact. Mirrors Android v1.0.1: "label texte + timeout 60 s sur la
barre de progression export/import".
*/
function armLongRunningTimer(inRoot: HTMLElement): () => void
{
	const vId = window.setTimeout(() => {
		setStatus(inRoot, 'L\'opération prend plus de temps que prévu — tu peux laisser tourner.', 'info')
	}, kExportTimeoutMs)
	return () => { window.clearTimeout(vId) }
}

/*
Compute and render stats from IDB. Re-callable after an import to refresh
the counts.
*/
/*
Query the storage estimate and reveal the quota banner when usage
crosses kQuotaWarnFraction. Runs once on mount; the figures are
cheap (a single platform call) but evolve slowly enough that we
don't bother refreshing per-tick.
*/
async function hydrateStorageWarning(inRoot: HTMLElement): Promise<void>
{
	const vBanner = inRoot.querySelector<HTMLElement>('#profile-quota-banner')
	const vText = inRoot.querySelector<HTMLElement>('#profile-quota-banner-text')
	const vCta = inRoot.querySelector<HTMLButtonElement>('#profile-quota-banner-cta')
	if (!vBanner || !vText || !vCta) return

	const vEstimate = await estimateStorage()
	if (!vEstimate || vEstimate.usageFraction < kQuotaWarnFraction)
	{
		vBanner.setAttribute('data-open', 'false')
		return
	}

	const vUsageMb = (vEstimate.usageBytes / kMegaByte).toFixed(0)
	const vQuotaMb = (vEstimate.quotaBytes / kMegaByte).toFixed(0)
	const vPercent = Math.round(vEstimate.usageFraction * 100)
	vText.textContent = `Tu utilises ${vUsageMb} Mo sur ${vQuotaMb} Mo (${vPercent} %). Exporte un ZIP pour ne rien perdre si le système doit libérer de l'espace.`
	vBanner.setAttribute('data-open', 'true')

	vCta.addEventListener('click', () => {
		// Forward to the existing export button so the actual flow lives
		// in one place. Falling through to the same setStatus path keeps
		// success / error feedback consistent.
		inRoot.querySelector<HTMLElement>('#btn-export')?.click()
	})
}

/*
Surface the dataset snapshot version in the profile footer. Reads the
`generatedAt` ISO 8601 timestamp baked into dataset-stats.json by
tools/build_dataset.py and renders it as "Snapshot OpenData du 9 mai
2026". Falls back to a generic line if the field is absent (older
dataset bundles pre-dating the field).
*/
async function hydrateDatasetVersion(inRoot: HTMLElement): Promise<void>
{
	const vEl = inRoot.querySelector<HTMLElement>('#profile-footer-dataset')
	if (!vEl) return
	try
	{
		const vStats = await ensureDatasetStats()
		if (!vStats.generatedAt)
		{
			vEl.textContent = 'Snapshot OpenData (date inconnue)'
			return
		}
		const vDate = new Date(vStats.generatedAt)  // ISO 8601 with Z suffix → reliable cross-browser parse
		if (Number.isNaN(vDate.getTime()))
		{
			vEl.textContent = 'Snapshot OpenData (date invalide)'
			return
		}
		vEl.textContent = `Snapshot OpenData du ${kFrenchLongDate.format(vDate)}`
	}
	catch
	{
		// Soft degradation: dataset-stats fetch failed (offline, missing
		// asset). Nothing actionable from the user's side, hide the line.
		vEl.textContent = ''
	}
}

/*
Section « Activité récente » (timeline des captures) retirée —
feature PWA-only sans équivalent Android, arbitrage parité stricte.
*/


async function hydrateStats(inRoot: HTMLElement): Promise<void>
{
	const vGrid = inRoot.querySelector<HTMLElement>('#stats-grid')
	if (!vGrid) return
	const vEmpty = inRoot.querySelector<HTMLElement>('#profile-empty')
	const vHeading = inRoot.querySelector<HTMLElement>('#progression-heading')
	const vCard = inRoot.querySelector<HTMLElement>('#stats-card')
	const vDays = inRoot.querySelector<HTMLElement>('#days-line')
	try
	{
		const vStats = await loadStats()
		// 0-captures — parité Android : l'EmptyState remplace DaysSinceLine +
		// Progression hors de toute card (les sections Badges/Infos/Sauvegarde
		// restent visibles en dessous). Pas de CTA (parité stricte).
		if (vStats.totalCaptures === 0)
		{
			if (vEmpty)
			{
				vEmpty.hidden = false
				vEmpty.innerHTML = renderEmptyState({
					variant: 'profile',
					title: 'Ton aventure commence ici',
					body: "Approche-toi d'un arbre, capture-le pour révéler son espèce. Tes stats et tes badges s'écriront ici au fil des captures.",
				})
			}
			if (vHeading) vHeading.hidden = true
			if (vCard) vCard.hidden = true
			if (vDays) vDays.hidden = true
			return
		}
		if (vEmpty) { vEmpty.hidden = true; vEmpty.innerHTML = '' }
		if (vHeading) vHeading.hidden = false
		if (vCard) vCard.hidden = false
		if (vDays)
		{
			// Parité DaysSinceLine Android (0 / 1 / N jours).
			const vD = vStats.daysSinceFirst
			vDays.textContent = vD === 0
				? "Première capture aujourd'hui"
				: vD === 1 ? '1 jour depuis ta première capture' : `${vD} jours depuis ta première capture`
			vDays.hidden = false
		}
		vGrid.innerHTML = renderProgressionCard(vStats)
	}
	catch (vErr)
	{
		const vMsg = vErr instanceof Error ? vErr.message : 'Erreur inconnue'
		vGrid.innerHTML = `<span class="profile-stats-error">⚠ ${escapeHtml(vMsg)}</span>`
	}
}

async function loadStats(): Promise<Stats>
{
	const vCaptures = await listCaptures()  // chronological

	// Pré-load tous les datasets nécessaires aux 7 barres en parallèle.
	// L'éval badges (preview « Derniers badges ») consomme déjà tout cet
	// ensemble, donc loadStats partage le coût.
	try
	{
		await Promise.all([
			ensureSpeciesIndex(),
			ensureGenreInfo(),
			ensureArrSpecies(),
			ensureArbreAttrs(),
		])
	}
	catch
	{
		// Soft degradation — les helpers retournent des fallbacks neutres
		// (allGenres() vide, arrFor() null, etc.).
	}

	// Le statut « remarquable » d'une capture est dérivé du dataset (la PWA
	// ne stocke pas le flag, contrairement à CaptureEntity Android) :
	// arbreId ∈ ids remarquables des features. Soft fail si le geojson
	// 32 Mo n'est pas chargé : vRemIds vide → captures toutes traitées
	// comme ordinaires, et la barre 1 se masque (numérateur 0 — parité
	// Android `ProgressionState(numerator = null)` tant que la DB n'a pas
	// répondu).
	let vRemIds: ReadonlySet<number> = new Set<number>()
	let vOrdinaryCounts: ReadonlyMap<number, number> | null = null
	try
	{
		const vFeatures = await ensureArbresFeatures()
		vRemIds = new Set(vFeatures.filter((inF) => inF.remarquable).map((inF) => inF.id))
		vOrdinaryCounts = await ordinaryCountsBySpecies()
	}
	catch
	{
		// laisse vRemIds vide / vOrdinaryCounts null
	}

	// Pass single-shot sur les captures pour les compteurs distincts. Les
	// sets d'espèces EXCLUENT les captures remarquables — parité DAO
	// Android `capturedSpeciesIndices` (WHERE remarquable = 0) : capturer
	// un remarquable ne « capture » pas son espèce, ni ne touche son genre.
	// Les arrondissements comptent toutes les captures (parité Android
	// `toutesLesCaptures`).
	const vRemarquables = new Set<number>()   // barre 2 — remarquables capturés (arbreId)
	const vIdentifiedSks = new Set<number>()  // barre 3 num — distinct sk identifiés
	const vCapturedSks = new Set<number>()    // barres 1 + 4 — sks ordinaires capturés
	const vArrSlugs = new Set<string>()       // barre 6 — Arrondissements visités (hors 'other')
	for (const vCap of vCaptures)
	{
		if (vRemIds.has(vCap.arbreId))
		{
			vRemarquables.add(vCap.arbreId)
		}
		else
		{
			vCapturedSks.add(vCap.speciesIndex)
			const vName = speciesNameFor(vCap.speciesIndex)
			if (vName && !vName.unknownSpecies) vIdentifiedSks.add(vCap.speciesIndex)
		}
		const vArr = arrFor(vCap.arbreId)
		if (vArr && vArr.kind !== 'other') vArrSlugs.add(arrKeyIdSlug(vArr))
	}

	// Barre 1 — « Arbres déverrouillés », parité Android
	// `ArbreRepository.nombreArbresDecouverts` : un arbre ordinaire se
	// déverrouille dès que son espèce est capturée, un remarquable
	// uniquement via sa propre capture. Pas de double-comptage : les
	// remarquables sont exclus de vOrdinaryCounts par construction.
	let vArbresUnlocked = 0
	if (vOrdinaryCounts)
	{
		vArbresUnlocked = vRemarquables.size
		for (const vSk of vCapturedSks) vArbresUnlocked += vOrdinaryCounts.get(vSk) ?? 0
	}
	// v1.3.1 — barre Genres découverts : « touched » semantics, parité
	// Android `allGenres().count { genreHasAnyCapture(g, capturedSks) }`.
	// Effet de bord vs implémentation précédente : une capture
	// `(Genista|Vitex|Ziziphus) sp.` fait monter le compteur (le genre
	// est touché). Le pseudo-genre « Non spécifié » est déjà exclu
	// d'`allGenres()` (parité Android `genresAllUseful`).
	const vGenresDecouverts = allGenres().filter((g) => genreHasAnyCapture(g, vCapturedSks)).length

	// Compteurs Familier — même éval badges que la section "Derniers
	// badges" (on recalcule ici parce que les 2 hydrateurs runnent en
	// parallèle ; coût ~10-50 ms sur captures < 1k, acceptable).
	let vGenresComplets = 0
	let vArrComplets = 0
	try
	{
		const vInfo = await ensureSpeciesInfo()
		const vFeatures = await ensureArbresFeatures()
		const vById = new Map<number, typeof vFeatures[number]>()
		for (const vCap of vCaptures)
		{
			const vF = vFeatures.find((inF) => inF.id === vCap.arbreId)
			if (vF) vById.set(vCap.arbreId, vF)
		}
		const vStates = evaluateBadges({ captures: vCaptures, arbresById: vById, speciesInfo: vInfo })
		for (const vS of vStates)
		{
			if (vS.kind !== 'binary' || vS.unlockedAt === null) continue
			if (vS.def.id.startsWith(FAMILIER_GENRE_PREFIX)) vGenresComplets++
			else if (vS.def.id.startsWith(FAMILIER_ARR_PREFIX)) vArrComplets++
		}
	}
	catch
	{
		// laisse compteurs Familier à 0
	}

	// Dataset totals — soft fall-back à 0 si l'asset n'est pas chargé
	// (la barre se masque alors via `value <= 0 || total <= 0`).
	let vTotalArbres = 0
	let vTotalRemarquables = 0
	let vTotalEspecesIdentifiees = 0
	try
	{
		const vDatasetStats = await ensureDatasetStats()
		vTotalArbres = vDatasetStats.totalArbres ?? 0
		vTotalRemarquables = vDatasetStats.totalRemarquables ?? 0
		vTotalEspecesIdentifiees = vDatasetStats.totalEspecesIdentifiees ?? vDatasetStats.totalEspeces ?? 0
	}
	catch
	{
		// soft degradation
	}

	const vTotalGenres = allGenres().length
	const vTotalGenresMajeurs = familierGenres().length

	const vFirstTs = vCaptures.length > 0 ? vCaptures[0]!.timestamp : 0
	const vDaysSince = vFirstTs > 0 ? Math.floor((Date.now() - vFirstTs) / kMillisPerDay) : 0

	return {
		totalCaptures: vCaptures.length,
		arbresUnlocked: vArbresUnlocked,
		totalArbres: vTotalArbres,
		remarquablesCount: vRemarquables.size,
		totalRemarquables: vTotalRemarquables,
		distinctIdentifiedSpecies: vIdentifiedSks.size,
		totalEspecesIdentifiees: vTotalEspecesIdentifiees,
		genresDecouverts: vGenresDecouverts,
		totalGenres: vTotalGenres,
		genresComplets: vGenresComplets,
		totalGenresMajeurs: vTotalGenresMajeurs,
		arrVisites: vArrSlugs.size,
		totalArrVisites: 22,
		arrComplets: vArrComplets,
		totalArrComplets: arrKeysWithRemarquables().length,
		daysSinceFirst: vDaysSince,
	}
}

/*
Render the ProgressionCard contents — DaysSinceLine + 7 barres M3
alignées Android v1.2.0 (ProfileScreen.kt:494-500). Chaque barre se
masque seule via `renderProgressBar` si `value <= 0 || total <= 0`
(parité Compose `if (value > 0)`).

Ordre Android (`ProgressionCard:481-500`) :
  1. Arbres déverrouillés                          (nombreArbresDecouverts / 217 264)
  2. Remarquables capturés                         (Set remarquable id / 183)
  3. Espèces capturées                             (Set sk identified, hors remarquables / 784)
  4. Genres découverts (touched)                   (allGenres + genreHasAnyCapture / 204)
  5. Genres complétés                              (Familier genre / 26)
  6. Arrondissements visités                       (Set ArrKey / 22)
  7. Arrondissements complétés                     (Familier arr / 20 — v1.4.0 : 2e et 6e exclus, sans remarquables)
*/
function renderProgressionCard(inStats: Stats): string
{
	// DaysSinceLine rendue hors card par hydrateStats (#days-line) —
	// parité Android où elle vit au-dessus du heading « Progression ».
	return renderProgressBar({ label: 'Arbres déverrouillés',       value: inStats.arbresUnlocked,           total: inStats.totalArbres,             metricId: 'arbres-deverrouilles' })
		+ renderProgressBar({ label: 'Remarquables capturés',      value: inStats.remarquablesCount,        total: inStats.totalRemarquables,       metricId: 'remarquables-captures' })
		+ renderProgressBar({ label: 'Espèces capturées',          value: inStats.distinctIdentifiedSpecies, total: inStats.totalEspecesIdentifiees, metricId: 'especes-capturees' })
		+ renderProgressBar({ label: 'Genres découverts',          value: inStats.genresDecouverts,         total: inStats.totalGenres,             metricId: 'genres-decouverts' })
		+ renderProgressBar({ label: 'Genres complétés',           value: inStats.genresComplets,           total: inStats.totalGenresMajeurs,      metricId: 'genres-completes' })
		+ renderProgressBar({ label: 'Arrondissements visités',    value: inStats.arrVisites,               total: inStats.totalArrVisites,         metricId: 'arrondissements-visites' })
		+ renderProgressBar({ label: 'Arrondissements complétés',  value: inStats.arrComplets,              total: inStats.totalArrComplets,        metricId: 'arrondissements-completes' })
}

function formatExportSummary(inResult: ExportResult): string
{
	const vMb = (inResult.bytes / (1024 * 1024)).toFixed(1)
	const vMissing = inResult.photosMissing > 0 ? `, ${inResult.photosMissing} photo(s) manquante(s)` : ''
	return `✓ Backup créé · ${inResult.captureCount} capture(s), ${inResult.photoCount} photo(s) (${vMb} Mo)${vMissing}.`
}

function formatImportSummary(inResult: ImportResult): string
{
	const vMissing = inResult.photosMissing > 0 ? `, ${inResult.photosMissing} photo(s) manquante(s)` : ''
	return `✓ Import OK · ${inResult.importedCount} ajoutée(s), ${inResult.skippedDuplicates} doublon(s)${vMissing}.`
}

function setStatus(inRoot: HTMLElement, inText: string, inVariant: 'info' | 'success' | 'error'): void
{
	const vEl = inRoot.querySelector<HTMLElement>('#backup-status')
	if (!vEl) return
	vEl.textContent = inText
	vEl.dataset.variant = inVariant
}

function escapeHtml(inText: string): string
{
	return inText
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
}

const kRecentBadgeSlots: number = 3  // "Derniers badges" Android v1.0.1 — row of 3 most-recently unlocked

/*
Render the "Derniers badges" row (3 most-recently unlocked) above the
"Voir tous les badges" CTA. Masquée si vide — when no badges are
unlocked, we hide the strip and only show the CTA so the section
doesn't look broken on a fresh profile. Pas de compteur ni de texte :
parité Android (ProfileScreen.kt = titre → grille → CTA).
*/
async function hydrateBadgesPreview(inRoot: HTMLElement): Promise<void>
{
	const vPreview = inRoot.querySelector<HTMLElement>('#badges-preview')
	if (!vPreview) return

	try
	{
		// Load all 6 datasets in parallel — fullCatalog needs
		// speciesIndex + genreInfo + arrSpecies, the evaluator
		// additionally needs arbreAttrs (Familier arr + Géant / Bonsaï…)
		// and speciesInfo (raretés exactes 1..5).
		const [vCaptures, vFeatures, vInfo] = await Promise.all([
			listCaptures(),
			ensureArbresFeatures(),
			ensureSpeciesInfo(),
			ensureSpeciesIndex(),
			ensureGenreInfo(),
			ensureArrSpecies(),
			ensureArbreAttrs(),
		])
		const vById = new Map<number, typeof vFeatures[number]>()  // arbreId -> feature, only for ones the user touched
		for (const vCap of vCaptures)
		{
			const vF = vFeatures.find((inF) => inF.id === vCap.arbreId)
			if (vF) vById.set(vCap.arbreId, vF)
		}
		const vStates = evaluateBadges({ captures: vCaptures, arbresById: vById, speciesInfo: vInfo })
		const vRecent = pickRecentBadges(vStates)
		if (vRecent.length === 0)
		{
			vPreview.style.display = 'none'
			vPreview.innerHTML = ''
		}
		else
		{
			vPreview.style.display = ''
			vPreview.innerHTML = vRecent.map(renderMiniBadge).join('')
		}
	}
	catch (vErr)
	{
		// Pas de canal d'erreur visible dans cette section (parité Android) :
		// la strip disparaît, il reste le CTA vers l'écran Badges.
		console.warn('Badge preview failed', vErr)
		vPreview.style.display = 'none'
		vPreview.innerHTML = ''
	}
}

/*
Pick the kRecentBadgeSlots most-recent unlock events. Progressive tiers
count independently (one Marcheur palier and the previous Marcheur
palier are two distinct unlocks in the timeline), so we flatten the
catalogue first.
*/
function pickRecentBadges(inStates: BadgeState[]): BadgeUnlock[]
{
	return flattenBadgeUnlocks(inStates)
		.sort((inA, inB) => inB.unlockedAt - inA.unlockedAt)
		.slice(0, kRecentBadgeSlots)
}

/*
Long-press detector sur les barres de ProgressionCard.
Délégation au niveau #stats-grid pour ne pas recâbler après chaque
re-render. Seuil 500 ms / drift max 10 px (mirror Android
combinedClickable). Délègue à openHistorySheet, qui rebuilt la pipeline
async (listCaptures + ensureArbresFeatures + evaluateBadges +
buildHistorySeries).
*/
const kLongPressMs = 500
const kLongPressDriftPx = 10

function wireProgressionLongPress(inRoot: HTMLElement): void
{
	const vGrid = inRoot.querySelector<HTMLElement>('#stats-grid')
	if (!vGrid) return

	let vTimer: number | null = null
	let vStartX = 0
	let vStartY = 0
	let vTargetMetric: ProgressionMetric | null = null

	const vCancel = (): void => {
		if (vTimer !== null) { window.clearTimeout(vTimer); vTimer = null }
		vTargetMetric = null
	}

	vGrid.addEventListener('pointerdown', (inEv: PointerEvent) => {
		const vBar = (inEv.target as HTMLElement | null)?.closest<HTMLElement>('.progression-bar[data-metric]')
		if (!vBar) return
		const vMetric = vBar.dataset.metric as ProgressionMetric | undefined
		if (!vMetric) return
		vStartX = inEv.clientX
		vStartY = inEv.clientY
		vTargetMetric = vMetric
		vTimer = window.setTimeout(() => {
			if (vTargetMetric) void openHistorySheet(vTargetMetric)
			vCancel()
		}, kLongPressMs)
	})
	vGrid.addEventListener('pointermove', (inEv: PointerEvent) => {
		if (vTimer === null) return
		const vDx = inEv.clientX - vStartX
		const vDy = inEv.clientY - vStartY
		if (vDx * vDx + vDy * vDy > kLongPressDriftPx * kLongPressDriftPx) vCancel()
	})
	vGrid.addEventListener('pointerup', vCancel)
	vGrid.addEventListener('pointercancel', vCancel)
	vGrid.addEventListener('pointerleave', vCancel)
}

async function openHistorySheet(inMetric: ProgressionMetric): Promise<void>
{
	if (fHistoryController) return  // déjà ouvert
	try
	{
		const [{ mountHistorySheet }] = await Promise.all([
			import('./components/HistorySheet'),
			ensureSpeciesIndex(),
			ensureGenreInfo(),
			ensureArrSpecies(),
			ensureArbreAttrs(),
		])
		const [vCaptures, vFeatures, vInfo] = await Promise.all([
			listCaptures(),
			ensureArbresFeatures(),
			ensureSpeciesInfo(),
		])
		const vById = new Map<number, typeof vFeatures[number]>()
		for (const vCap of vCaptures)
		{
			const vF = vFeatures.find((inF) => inF.id === vCap.arbreId)
			if (vF) vById.set(vCap.arbreId, vF)
		}
		const vStates = evaluateBadges({ captures: vCaptures, arbresById: vById, speciesInfo: vInfo })
		const vOrdinaryCounts = await ordinaryCountsBySpecies()  // memoizé, features déjà chargées ci-dessus
		const vSeries = buildHistorySeries(inMetric, { captures: vCaptures, arbresById: vById, badgeStates: vStates, ordinaryCountBySk: vOrdinaryCounts })
		const vDismiss = (): void => {
			if (fHistoryController) { fHistoryController.destroy(); fHistoryController = null }
		}
		fHistoryController = mountHistorySheet(document.body, { series: vSeries, onDismiss: vDismiss })
	}
	catch (vErr)
	{
		// soft fail — pas de bouton retry, le user peut juste re-long-press
		console.warn('History sheet build failed', vErr)
	}
}

function renderMiniBadge(inUnlock: BadgeUnlock): string
{
	const vGlyph = glyphFor(inUnlock.def)
	// For progressive tiers we show the tier label (e.g. "Centurion"),
	// for binaries we show the badge label (e.g. "Géant").
	return `
		<div class="profile-badge-mini" data-unlocked="true" title="${escapeHtml(inUnlock.label)}">
			<div class="profile-badge-mini-glyph" aria-hidden="true">${vGlyph}</div>
			<div class="profile-badge-mini-label">${escapeHtml(inUnlock.label)}</div>
		</div>
	`
}
